import { ReactNode, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdCloseFullscreen, MdOpenInFull } from 'react-icons/md';
import { Design, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { useDtcCommerceMmmData } from '../../context/DtcCommerceMmmDataContext';
import {
  EXPAND_BUTTON_CLASS,
  EXPANDED_CHART_CLASS,
  ExpandableChartNameType
} from './channel-drawer-constants';

interface ChannelDrawerExpandableChartProps {
  // Analytics label for the modal_opened event.
  chartName: ExpandableChartNameType;
  disabled?: boolean;
  // Called twice: once for the drawer, and once more for the enlarged copy in the modal while it
  // is open. Both copies render from the caller's state, so a toggle flipped in one view is still
  // flipped in the other. The caller places `expandButton` at the far right of its header actions.
  children: (isExpanded: boolean, expandButton: ReactNode) => ReactNode;
}

export const ChannelDrawerExpandableChart = ({
  chartName,
  disabled = false,
  children
}: ChannelDrawerExpandableChartProps) => {
  const { config } = useDtcCommerceMmmData();
  const [isExpanded, setIsExpanded] = useState(false);

  const expand = () => {
    setIsExpanded(true);
    Mixpanel.track(MxE.ModalOpened, { page: config.pageType, modal: chartName });
  };
  const collapse = () => setIsExpanded(false);

  // Rendered inside the drawer's tree, so a backdrop click or Escape closes only this modal — the
  // shared modal manager hands both to the topmost dialog — and the drawer stays open beneath it.
  return (
    <>
      {children(false, <ExpandButton isExpanded={false} disabled={disabled} onClick={expand} />)}
      <Modal
        show={isExpanded}
        onHide={collapse}
        centered
        dialogClassName='channel-drawer-expanded-dialog'
        contentClassName={EXPANDED_CHART_CLASS}
        aria-label={`${chartName} (expanded)`}
      >
        {children(true, <ExpandButton isExpanded onClick={collapse} />)}
      </Modal>
    </>
  );
};

interface ExpandButtonProps {
  isExpanded: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const ExpandButton = ({ isExpanded, disabled, onClick }: ExpandButtonProps) => (
  <IconButton
    aria-label={isExpanded ? 'Collapse chart' : 'Expand chart'}
    className={EXPAND_BUTTON_CLASS}
    design={Design.NEUTRAL}
    size={Size.MD}
    disabled={disabled}
    onClick={onClick}
  >
    {isExpanded ? <MdCloseFullscreen /> : <MdOpenInFull />}
  </IconButton>
);
